import { useEffect, useRef } from 'react';
import { Palette, Zap, Search, Smartphone, Users } from 'lucide-react';

const advantages = [
  {
    icon: Palette,
    title: 'Design professionnel',
    description: 'Un design unique et moderne qui reflète votre identité.'
  },
  {
    icon: Zap,
    title: 'Chargement ultra rapide',
    description: 'Performance optimale pour une meilleure expérience utilisateur.'
  },
  {
    icon: Search,
    title: 'Optimisation SEO',
    description: 'Visibilité maximale sur les moteurs de recherche.'
  },
  {
    icon: Smartphone,
    title: 'Compatible mobile',
    description: 'Parfaitement adapté à tous les appareils.'
  },
  {
    icon: Users,
    title: 'Accompagnement personnalisé',
    description: 'Un suivi dédié tout au long de votre projet.'
  }
];

export const AdvantagesSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = sectionRef.current?.querySelectorAll('.fade-in');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      data-testid="advantages-section"
      className="py-24 md:py-32 bg-[#F5F5F7]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 fade-in">
          <span className="inline-block text-xs font-medium text-[#1a365d] uppercase tracking-wider mb-4">
            Nos atouts
          </span>
          <h2 
            data-testid="advantages-title"
            className="font-['Manrope'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
          >
            Pourquoi choisir WO Consulting
          </h2>
          <p className="text-base text-[#4A4A4A] max-w-2xl mx-auto">
            Une expertise suisse au service de votre réussite digitale.
          </p>
        </div>

        {/* Advantages Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {advantages.map((advantage, index) => (
            <div
              key={advantage.title}
              data-testid={`advantage-${index}`}
              className={`fade-in bg-white p-8 rounded-2xl border border-[#D0D0D0] hover:border-[#1a365d] transition-all duration-300 hover:-translate-y-1 stagger-${index + 1}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="w-12 h-12 bg-[#1a365d]/10 rounded-xl flex items-center justify-center mb-6">
                <advantage.icon className="text-[#1a365d]" size={24} />
              </div>
              <h3 className="font-['Manrope'] text-lg font-bold text-[#1A1A1A] mb-2">
                {advantage.title}
              </h3>
              <p className="text-sm text-[#4A4A4A] leading-relaxed">
                {advantage.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;
